import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Gift, ArrowRight, Sparkles, CheckCircle, Phone, Mail } from 'lucide-react';
import { useBooking } from '../context/BookingContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PromoPopup = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [showFloatingIcon, setShowFloatingIcon] = useState(false);
  const [settings, setSettings] = useState(null);
  const [userClosedPopup, setUserClosedPopup] = useState(false);
  const { openBooking } = useBooking();
  const location = useLocation();

  const isAdminPage = location.pathname.startsWith('/admin');

  useEffect(() => {
    const closed = sessionStorage.getItem('promo_popup_user_closed') === 'true';
    setUserClosedPopup(closed);
  }, []);

  useEffect(() => {
    if (isAdminPage) return;
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${API_URL}/api/cms/promo-popup`);
        if (res.ok) {
          const data = await res.json();
          setSettings(data);
        }
      } catch (e) {
        console.log('Failed to fetch promo settings');
      }
    };
    fetchSettings();
  }, [isAdminPage]);

  const isReturningUser = () => {
    return (
      localStorage.getItem('contact_submitted') === 'true' ||
      localStorage.getItem('booking_submitted') === 'true' ||
      localStorage.getItem('pricing_unlocked') === 'true'
    );
  };

  useEffect(() => {
    if (isAdminPage) return;
    
    const isEnabled = settings?.enabled !== false;
    const returningUser = isReturningUser();
    
    if (returningUser) {
      setShowFloatingIcon(true);
      return;
    }

    if (userClosedPopup) {
      setShowFloatingIcon(true);
      return;
    }

    if (isEnabled && !returningUser) {
      const delay = (settings?.display_delay || 2) * 1000;
      const timer = setTimeout(() => {
        setIsPopupOpen(true);
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [settings, isAdminPage, userClosedPopup]);

  const closePopup = () => {
    setIsPopupOpen(false);
    setShowFloatingIcon(true);
    setUserClosedPopup(true);
    sessionStorage.setItem('promo_popup_user_closed', 'true');
  };

  const handleClaim = () => {
    closePopup();
    openBooking();
  };

  if (isAdminPage) return null;

  const discountPercent = settings?.discount_percent || 10;
  const headline = settings?.headline || 'Exclusive Offer!';
  const phone = settings?.phone || '1800 951 GET';
  const email = settings?.email || 'info@getcare360.com.au';

  return (
    <>
      {/* Floating Button */}
      {showFloatingIcon && !isPopupOpen && (
        <button
          onClick={() => setIsPopupOpen(true)}
          className="fixed bottom-6 left-6 z-[9998] flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-3 rounded-full shadow-lg active:scale-95 transition-transform"
          data-testid="promo-floating-btn"
        >
          <Gift size={20} />
          <span className="font-bold text-sm">{discountPercent}% OFF</span>
          <Sparkles size={16} />
        </button>
      )}

      {/* Popup Modal */}
      {isPopupOpen && (
        <div className="fixed inset-0 z-[9999] overflow-y-auto">
          {/* Dark Overlay - Tappable to close */}
          <div 
            className="fixed inset-0 bg-black/70"
            onClick={closePopup}
            onTouchEnd={closePopup}
          />
          
          {/* Popup Content */}
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div 
              className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
            >
              {/* Top Close Bar - Always visible */}
              <div className="bg-gray-100 px-4 py-3 flex items-center justify-between border-b">
                <span className="text-xs text-gray-500 font-medium">Special Offer</span>
                <button
                  onClick={closePopup}
                  onTouchEnd={(e) => { e.stopPropagation(); closePopup(); }}
                  className="flex items-center gap-1 text-gray-600 font-medium text-sm px-3 py-2 rounded-lg bg-gray-200 active:bg-gray-300"
                  data-testid="promo-close-btn"
                >
                  <X size={18} strokeWidth={2.5} />
                  <span>Close</span>
                </button>
              </div>

              {/* Header */}
              <div className="bg-gradient-to-br from-slate-800 via-slate-900 to-gray-900 px-6 py-6 text-center text-white">
                <Gift className="mx-auto mb-2" size={32} />
                <h2 className="text-xl font-bold mb-3">{headline}</h2>
                <div className="inline-flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-full font-bold text-lg">
                  <Sparkles size={18} />
                  {discountPercent}% OFF
                  <Sparkles size={18} />
                </div>
              </div>

              {/* Content */}
              <div className="px-5 py-4">
                <h3 className="text-base font-bold text-gray-800 text-center mb-1">
                  Book a Demo & Save Big!
                </h3>
                <p className="text-sm text-gray-500 text-center mb-4">
                  Get {discountPercent}% off Development & Hosting
                </p>

                {/* Benefits */}
                <div className="space-y-2 mb-4">
                  {[
                    'Save thousands on development',
                    'Own it forever - no subscriptions',
                    'Your branded NDIS platform',
                    'No hidden fees'
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle size={16} className="text-teal-500 flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>

                {/* Contact */}
                <div className="flex justify-center gap-4 mb-4 text-sm">
                  <a href={`tel:${phone.replace(/\s/g, '')}`} className="flex items-center gap-1 text-blue-600">
                    <Phone size={14} />
                    <span>{phone}</span>
                  </a>
                </div>

                {/* CTA Button */}
                <button
                  onClick={handleClaim}
                  onTouchEnd={(e) => { e.stopPropagation(); handleClaim(); }}
                  className="w-full bg-gradient-to-r from-slate-800 to-slate-700 text-white font-bold py-4 rounded-lg flex items-center justify-center gap-2 active:opacity-90 mb-3"
                  data-testid="promo-book-demo-btn"
                >
                  <Gift size={20} />
                  Claim Your {discountPercent}% Discount
                  <ArrowRight size={20} />
                </button>

                {/* Secondary Close */}
                <button
                  onClick={closePopup}
                  onTouchEnd={(e) => { e.stopPropagation(); closePopup(); }}
                  className="w-full text-gray-500 font-medium py-3 text-sm active:text-gray-700 flex items-center justify-center gap-1"
                  data-testid="promo-no-thanks-btn"
                >
                  <X size={16} />
                  No thanks, close this
                </button>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-5 py-3 text-center text-xs text-gray-500 border-t">
                Trusted by <span className="font-semibold text-gray-700">100+ NDIS providers</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PromoPopup;
